import {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef
} from 'react';
import { WidgetProps, WidgetRef } from '../type';
import { ChatMessageData, SystemMessageData } from '@/types';
import { sleep } from '@/lib/utils';

interface Particle {
  x: number;
  y: number;
  color: string;
  alpha: number;
  velX: number;
  velY: number;
  gravity: number;
  update: () => void;
  draw: (ctx: CanvasRenderingContext2D) => void;
}

interface Firework {
  x: number;
  y: number;
  targetY: number;
  color: string;
  exploded: boolean;
  particles: Particle[];
  speed: number;
  update: () => void;
  draw: (ctx: CanvasRenderingContext2D) => void;
}

const audioUtil = {
  playLaunchSound: (ctx: AudioContext) => {
    const bufferSize = ctx.sampleRate * 0.5; // 0.5 seconds
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    // Generate white noise
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    // Add bandpass filter to shape the hiss
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(800, ctx.currentTime);
    filter.Q.value = 1;

    // Fade out
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    noise.start();
    noise.stop(ctx.currentTime + 0.5);
  },

  playExplosionSound: (ctx: AudioContext) => {
    // White noise burst
    const bufferSize = ctx.sampleRate * 0.2; // 0.2 seconds
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-3 * i / bufferSize); // fading noise
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    // Rumble oscillator
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(40 + Math.random() * 60, ctx.currentTime); // Low rumble

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0.4, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);

    // Connect and play
    noise.connect(gainNode);
    osc.connect(gainNode);
    gainNode.connect(ctx.destination);

    noise.start();
    noise.stop(ctx.currentTime + 0.2);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
  }
}

const FireworksCanvas = forwardRef<WidgetRef, WidgetProps>(({ targetRef, isFullscreen }: WidgetProps, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fireworksRef = useRef<Firework[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const dimensions = useRef({ width: window.innerWidth, height: window.innerHeight });
  const animationFrameIdRef = useRef<number | null>(null);
  const isAnimatingRef = useRef(false);

  const playExplosionSound = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    audioUtil.playExplosionSound(audioCtxRef.current);
  };

  const playLaunchSound = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    audioUtil.playLaunchSound(audioCtxRef.current);
  };

  const createParticle = (
    x: number,
    y: number,
    angle: number,
    speed: number,
    color: string
  ): Particle => ({
    x,
    y,
    velX: Math.cos(angle) * speed,
    velY: Math.sin(angle) * speed,
    alpha: 1,
    color,
    gravity: 0.05,
    update() {
      this.x += this.velX;
      this.y += this.velY;
      this.velY += this.gravity;
      this.alpha -= 0.01;
    },
    draw(ctx: CanvasRenderingContext2D) {
      ctx.save();
      ctx.globalAlpha = this.alpha;
      ctx.beginPath();
      ctx.arc(this.x, this.y, 2, 0, Math.PI * 2);
      ctx.fillStyle = this.color;
      ctx.fill();
      ctx.restore();
    }
  });

  const createFirework = (x?: number, y?: number): Firework => {
    const { width: W, height: H } = dimensions.current;
    const fx = x ?? Math.random() * W;
    const fy = H;
    const targetY = y ?? Math.random() * H * 0.5;
    const color = `hsl(${Math.random() * 360}, 100%, 60%)`;

    playLaunchSound();

    return {
      x: fx,
      y: fy,
      targetY,
      color,
      exploded: false,
      particles: [],
      speed: -(Math.random() * 4 + 10),
      update() {
        if (!this.exploded) {
          this.y += this.speed;
          if (this.y <= this.targetY) {
            this.exploded = true;

            playExplosionSound();

            for (let i = 0; i < 100; i++) {
              const angle = Math.random() * 2 * Math.PI;
              const speed = Math.random() * 5 + 1;
              this.particles.push(createParticle(this.x, this.y, angle, speed, this.color));
            }
          }
        } else {
          this.particles.forEach(p => p.update());
          this.particles = this.particles.filter(p => p.alpha > 0);
        }
      },
      draw(ctx: CanvasRenderingContext2D) {
        if (!this.exploded) {
          ctx.beginPath();
          ctx.arc(this.x, this.y, 3, 0, Math.PI * 2);
          ctx.fillStyle = this.color;
          ctx.fill();
        } else {
          this.particles.forEach(p => p.draw(ctx));
        }
      }
    };
  };

  useImperativeHandle(ref, () => ({
    onMessage: (options: { message: SystemMessageData | ChatMessageData, x?: number, y?: number }) => {
      function extractNumber(str: string) {
        const match = str.match(/\d+/); // matches the first sequence of digits
        if (match) {
          return parseInt(match[0], 10);
        }
        return 0;
      }
      let num = extractNumber(options.message.message);
      if (num == null || num < 10 || num > 100) {
        num = Math.floor(Math.random() * 20) + 10;
      }
      async function start() {
        for (let i = 0; i < num; i++) {
          const delay = Math.random() * 50; // delay
          await sleep(delay);
          startAnimation();
          fireworksRef.current.push(createFirework(options.x, options.y));
        }
      }
      setTimeout(start, 500);
    },
  }));

  const startAnimation = () => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      if (!isAnimatingRef.current) return;

      animationFrameIdRef.current = requestAnimationFrame(animate);

      const { width: W, height: H } = dimensions.current;
      ctx.clearRect(0, 0, W, H);

      fireworksRef.current.forEach(fw => {
        fw.update();
        fw.draw(ctx);
      });

      fireworksRef.current = fireworksRef.current.filter(
        fw => !fw.exploded || fw.particles.length > 0
      );
      // optimization - stop when no fireworks left
      if (fireworksRef.current.length === 0) {
        ctx.clearRect(0, 0, W, H);
        stopAnimation();
      }
    };

    animationFrameIdRef.current = requestAnimationFrame(animate);
  }

  const stopAnimation = () => {
    if (animationFrameIdRef.current !== null) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
    isAnimatingRef.current = false;
  };

  const getBounds = () => {
    const container = targetRef?.current;
    if (isFullscreen) {
      return {
        left: 0,
        top: 0,
        width: window.innerWidth,
        height: window.innerHeight,
      };
    }
    return container?.getBoundingClientRect() ?? {
      left: 0,
      top: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    };
  };

  const resizeHandler = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    dimensions.current = getBounds();
    canvas.width = dimensions.current.width;
    canvas.height = dimensions.current.height;
  };

  useEffect(() => {
    resizeHandler();
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      stopAnimation();
    };
  }, []);

  return (
    <div className={`pointer-events-none z-50 ${isFullscreen ?
      'fixed top-0 left-0 w-screen h-screen' : 'absolute top-0 left-0 w-full h-full'} `}>
      <canvas
        ref={canvasRef}
        className={
          `pointer-events-none z-50 bg-transparent ${isFullscreen ?
            'fixed top-0 left-0 w-screen h-screen' :
            'absolute top-0 left-0 w-full h-full'}`
        }
      />
    </div>
  );
});

export default FireworksCanvas;
